// Portal helper — renders children into a top-of-document container so
// stacking-context gymnastics in the parent tree don't bury overlays
// under their own siblings.
//
// Sync render path: returns null when there's no document (SSR /
// node-without-DOM). Otherwise calls createPortal immediately so
// downstream useEffect hooks (focus traps, scroll locks) see the
// portaled tree on FIRST commit, not the second. This matches the
// expected behavior in client-only environments (Vite SPA + happy-
// dom test) — codebases that need true SSR portaling can ship their
// own gated wrapper.

import { type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface PortalProps {
  readonly children: ReactNode;
  /** Optional explicit container; defaults to document.body. */
  readonly container?: HTMLElement | null;
}

export function Portal({ children, container }: PortalProps): ReactNode {
  if (typeof document === "undefined") return null;
  const target = container ?? document.body;
  if (target === null) return null;
  return createPortal(children, target);
}
